import { zodResolver } from "@hookform/resolvers/zod";
import { 
  IAttractionShowtimeTicketForm,
  ISimpleEventDetail, ISubTicketForm, ISubTicketItem, PriceType, ShowTime, SupportedLanguage } from "interfaces";
import { Controller, useForm } from "react-hook-form";
import { SubTicketSchema } from "../schema";
import { HttpError } from "@refinedev/core";
import { LANGUAGES, PRICE_TYPES, WEEK_DAYS } from "utills/constants";
import { get } from "lodash";
import { Label } from "components/label";
import Select from "components/select";
import Input from "components/input";
import { TextEditor } from "components/textEditor";
import { SwitchButton } from "components/switchButton";
import { RightSideModal } from "components/rightSideModal";
import Button from "components/button";
import { IModalProps } from "components/modal";
import { useEffect } from "react";
import moment from "moment";
import DateTimePicker from "components/datetimePicker";
import { arrayCompare } from "utills/helpers";
import TimePicker from "components/timePicker";

interface SubTicketFormProps extends IModalProps {
  eventDetailData: ISimpleEventDetail;
  showTimes: ShowTime[];
  onSubmit: (data: ISubTicketForm) => void;
  defaultValues?: ISubTicketItem | null;
}

const getDefaultTemplate = (defaultValues: SubTicketFormProps["defaultValues"], eventDetailData: ISimpleEventDetail) => {
  return {
    type_name: defaultValues?.type_name ?? {
      en: "",
      vi: ""
    },
    short_description: defaultValues?.short_description ?? {
      en: "",
      vi: ""
    },
    price_type: defaultValues?.price_type ?? PriceType.PAID,
    original_price: defaultValues?.original_price ?? 0,
    price: defaultValues?.price ?? 0,
    sell_from: defaultValues
      ? defaultValues?.sell_from
        ? moment(defaultValues.sell_from).format("YYYY-MM-DD HH:mm:ss")
        : ""
      : moment().startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    sell_to: defaultValues
      ? defaultValues?.sell_to
        ? moment(defaultValues.sell_to).format("YYYY-MM-DD HH:mm:ss")
        : ""
      : moment().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
    is_soldout: defaultValues?.is_soldout ?? false,
    is_active: defaultValues?.is_active ?? true,
    showtime: defaultValues?.showtime ?? "",
    hide_in_ui: defaultValues?.hide_in_ui ?? false,
    total_for_sell: defaultValues?.total_for_sell ?? null,
    selling_days: defaultValues?.selling_days ?? [],
    weekly_event_days: defaultValues?.weekly_event_days ?? [],
    sold_out_before: defaultValues?.sold_out_before ?? "",
    detailed_ticket_information: defaultValues?.detailed_ticket_information ?? {
      en: "",
      vi: ""
    },
    ...(eventDetailData?.date_pricing?.enabled && {
      pricing_by_date_type: defaultValues?.pricing_by_date_type ?? undefined,
    }),
  }
}

const ALL_OPTION = {label: "All", value: "all"};

export const SubTicketForm = ({title, open, onClose, eventDetailData, showTimes, defaultValues, onSubmit}:SubTicketFormProps) => {
  const {
    control,
    handleSubmit,
    register,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IAttractionShowtimeTicketForm["sub_ticket"], HttpError, IAttractionShowtimeTicketForm["sub_ticket"]>({
    defaultValues: getDefaultTemplate(defaultValues, eventDetailData),
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        SubTicketSchema(eventDetailData)
      );
      return createResolver(values, context, options);
    },
  });

  useEffect(() => {
    if (open) {
      reset(getDefaultTemplate(defaultValues, eventDetailData));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])


  const _submit = (data: ISubTicketForm) => {
    onSubmit(data);
  }

  const showtimeArr = showTimes.map(showtime => ({
    label: get(showtime, "time", ""),
    value: get(showtime, "id", "")
  }))

  const weekdayArr = ([ALL_OPTION]).concat(WEEK_DAYS);
  
  return (
    <RightSideModal title={title ?? ""} open={open} onClose={onClose} footer={(
      <div className="w-full flex flex-row gap-4 items-center">
        <Button 
          className="w-full bg-white border-none text-brand-500 ring-1 ring-inset ring-brand-500 hover:bg-gray-50 active:bg-gray-100" 
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button className="w-full uppercase" onClick={handleSubmit(_submit)}>Save</Button>
      </div>
    )}>
      <div className="grid sm:grid-cols-6 gap-y-6 gap-x-4 p-4 sm:px-6">
        <div className="col-span-full">
          <Controller
            control={control}
            name="price_type"
            render={({ field: { onChange, value = "", ref } }) => {
              return (
                <div className="flex flex-row flex-wrap gap-x-3">
                  {PRICE_TYPES.map(type => (
                    <button
                      key={type.value}
                      type="button"
                      className={`rounded-md ${type.value === value ? "bg-[#A7B4FE] hover:bg-indigo-300 focus-visible:outline-indigo-300" : "bg-white ring-1 ring-inset ring-gray-300 hover:bg-gray-50"} px-6 py-3 text-sm font-semibold text-brand-500 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2`}
                      onClick={() => {
                        onChange(type.value)
                        if (type.value === PriceType.FREE) {
                          setValue("price", 0, {shouldValidate: true});
                          setValue("original_price", 0, {shouldValidate: true});
                          if (eventDetailData?.date_pricing?.enabled) {
                            setValue("pricing_by_date_type.holiday.price", 0, {shouldValidate: true});
                            setValue("pricing_by_date_type.holiday.original_price", 0, {shouldValidate: true});
                            setValue("pricing_by_date_type.weekend.price", 0, {shouldValidate: true});
                            setValue("pricing_by_date_type.weekend.original_price", 0, {shouldValidate: true});
                          }
                        }
                      }}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              );
            }}
          />
          {get(
            errors,
            `price_type`,
            ""
          ) ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `price_type.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>

        <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
          <p className="text-sm">Sub-ticket Info</p>
          {defaultValues?.id ? <p className="text-sm mt-1">Product ID: {defaultValues?.id}</p> : <></>}
        </div>
        {/* <div className="col-span-full flex items-center justify-between font-medium">
          <p className="text-sm">Category</p>
          <p>
            {get(
              ticketData, 
              `category.${firstLanguage}`
            )}
          </p>
        </div> */}

        <div className="col-span-full">
          <Label
            id="showtime"
            name="Showtime"
            required
          />
          <Controller
            control={control}
            name="showtime"
            render={({ field: { onChange, value = "", ref } }) => {
              return (
                <Select
                  key="showtime"
                  inputRef={ref}
                  options={showtimeArr}
                  id="showtime"
                  placeholder="Select showtime"
                  defaultValue={
                    showtimeArr.find(
                      (selected: { label: string; value: string }) =>
                        selected.value === value
                    ) ?? ""
                  }
                  value={showtimeArr.find(
                    (selected: { label: string; value: string }) =>
                      selected.value === value
                  )}
                  onChange={(selected: { label: string; value: string }) => {
                    onChange(selected?.value);
                  }}
                  error={get(errors, `showtime`, "")}
                />
              );
            }}
          />
          {get(
            errors,
            `showtime`,
            ""
          ) ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `showtime.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>

        <div className="col-span-full">
          <Label
            required
            id={`type_name_${eventDetailData.languages[0] ?? "en"}`}
            name="Ticket name displayed"
          />
          <div className="flex flex-col gap-y-4">
            {eventDetailData.languages.map((lang) => (
              <div key={lang}>
                <div className="relative" key={lang}>
                  <Input
                    id={`type_name_${lang}`}
                    type="text"
                    className="pr-11"
                    placeholder={`Ticket name displayed (${lang})`}
                    {...register(`type_name.${lang}`)}
                    error={get(
                      errors,
                      `type_name.${lang}.message`,
                      ""
                    )}
                  />
                  <img
                    src={`/images/flags/circle-${lang}.png`}
                    alt="Language Icon"
                    className="absolute top-1/2 right-3 -translate-y-1/2"
                  />
                </div>
                {get(
                  errors,
                  `type_name.${lang}`,
                  ""
                ) ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(
                        errors,
                        `type_name.${lang}.message`,
                        ""
                      )}
                    </span>
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
        <div className="col-span-full">
          <Label
            id={`short_description_${eventDetailData.languages[0] ?? "en"}`}
            name="Note below Ticket name"
          />
          <div className="flex flex-col gap-y-4">
            {eventDetailData.languages.map(
              (lang: SupportedLanguage) => (
                <div key={lang}>
                  <div
                    className={`w-fit flex flex-row items-center gap-x-1 p-2 -mb-1 rounded-tl-md rounded-tr-md border-magnolia-600 border-solid border-[1px] bg-magnolia-400 font-medium text-sm`}
                  >
                    <img
                      src={`/images/flags/circle-${lang}.png`}
                      alt="Language Icon"
                      className="w-4 shrink-0"
                    />
                    <p>
                      {
                        LANGUAGES.find(
                          (language) =>
                            language.value === lang
                        )?.label
                      }
                    </p>
                  </div>

                  <Controller
                    control={control}
                    name={`short_description.${lang}`}
                    render={({
                      field: { onChange, value = "" },
                    }) => (
                      <TextEditor
                        key={`short_description.${lang}`}
                        value={value ?? ""}
                        onChange={onChange}
                      />
                    )}
                  />

                  {get(
                    errors,
                    `short_description.${lang}`,
                    ""
                  ) ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(
                          errors,
                          `short_description.${lang}.message`,
                          ""
                        )}
                      </span>
                    </p>
                  ) : null}
                </div>
              )
            )}
          </div>
        </div>
        <div className="col-span-full">
          <Label
            id="weekly_event_days"
            name="Ticket usage date"
            required
          />
          <Controller
            control={control}
            name="weekly_event_days"
            defaultValue={[]}
            render={({ field: { onChange, value = [], ref } }) => {
              const isSelectedAll = arrayCompare(value, [...WEEK_DAYS.map(day => `${day.value}`)]);
              return (
                <Select
                  isMulti
                  key="weekly_event_days"
                  inputRef={ref}
                  options={isSelectedAll ? [] : weekdayArr}
                  id="weekly_event_days"
                  placeholder="Ticket usage date"
                  value={isSelectedAll ? [ALL_OPTION] : value.map((id: string) => {
                    return WEEK_DAYS.find((item) => item.value === id);
                  })}
                  onChange={(selected: { label: string; value: string }[]) =>
                    selected.find(
                      (item) => item.value === "all"
                    )
                      ? onChange([...WEEK_DAYS.map(day => `${day.value}`)])
                      : onChange(selected.map((item) => `${item.value}`))
                  }
                  error={get(errors,`weekly_event_days`,"")}
                />
              )
            }}
          />
          {get(errors, "weekly_event_days", "") ? (
            <p className="error-text">
              <span className="font-medium">
                {get(errors, `weekly_event_days.message`, "")}
              </span>
            </p>
          ) : null}
        </div>
        <div className="col-span-full">
          <Label
            id="selling_days"
            name="Date of ticket purchase"
            required
          />
          <Controller
            control={control}
            name="selling_days"
            defaultValue={[]}
            render={({ field: { onChange, value = [], ref } }) => {
              const isSelectedAll = arrayCompare(value, [...WEEK_DAYS.map(day => `${day.value}`)]);
              return (
                <Select
                  isMulti
                  key="selling_days"
                  inputRef={ref}
                  options={isSelectedAll ? [] : weekdayArr}
                  id="selling_days"
                  placeholder="Date of ticket purchase"
                  value={isSelectedAll ? [ALL_OPTION] : value.map((id: string) => {
                    return WEEK_DAYS.find((item) => item.value === id);
                  })}
                  onChange={(selected: { label: string; value: string }[]) =>
                    selected.find(
                      (item) => item.value === "all"
                    )
                      ? onChange([...WEEK_DAYS.map(day => `${day.value}`)])
                      : onChange(selected.map((item) => `${item.value}`))
                  }
                  error={get(errors,`selling_days`,"")}
                />
              )
            }}
          />
          {get(errors, "selling_days", "") ? (
            <p className="error-text">
              <span className="font-medium">
                {get(errors, `selling_days.message`, "")}
              </span>
            </p>
          ) : null}
        </div>
        <div className="col-span-full">
          <Label
            id="total_for_sell"
            name="Total for sell per day"
          />
          <Input
            id="total_for_sell"
            type="number"
            min={0}
            placeholder="Total for sell per day"
            {...register(`total_for_sell`, {
              valueAsNumber: true
            })}
            error={get(
              errors,
              `total_for_sell.message`,
              ""
            )}
          />
          {get(
            errors,
            `total_for_sell`,
            ""
          ) ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `total_for_sell.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>
        <div className="col-span-full">
          <Label id="sold_out_before" name="Sold-out before" />
          <Controller
            control={control}
            name="sold_out_before"
            render={({ field: { onChange, value = "" } }) => {
              return (
                <TimePicker
                  id="sold_out_before"
                  placeholder="Sold out before"
                  className="bg-white"
                  error={errors["sold_out_before"]}
                  value={value}
                  onChange={(date: any) => date === null ? onChange("") : onChange(moment(date).format("HH:mm"))}
                />
              );
            }}
          />
          
          {errors["sold_out_before"] ? (
            <p className="error-text">
              <span className="font-medium">
                {errors["sold_out_before"]?.message}
              </span>
            </p>
          ) : null}
        </div>
        <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
          <p className="text-sm">Price Info</p>
        </div>
        <div className="sm:col-span-3">
          <Label
            id="original_price"
            name="Original price"
            required
          />
          <div className="flex">
            <Input
              id="original_price"
              type="number"
              decimal={2}
              className="rounded-r-none"
              placeholder="Original price"
              disabled={watch("price_type") === PriceType.FREE}
              error={get(errors, "original_price")}
              {...register("original_price", {
                valueAsNumber: true
              })}  
            />
            <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
              {eventDetailData.currency}
            </span>
          </div>
          {get(
            errors,
            `original_price`,
            ""
          ) ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  "original_price.message",
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="price"
            name="Sell price"
            required
          />
          <div className="flex">
            <Input
              id="price"
              type="number"
              decimal={2}
              className="rounded-r-none"
              placeholder="Sell price"
              disabled={watch("price_type") === PriceType.FREE}
              error={get(errors, "price")}
              {...register("price", {
                valueAsNumber: true
              })}  
            />
            <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
              {eventDetailData.currency}
            </span>
          </div>
          {get(
            errors,
            `price`,
            ""
          ) ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  "price.message",
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>
        {/* Pricing by date type UI, only show if frequency_of_sell === USUALLY */}
        {eventDetailData?.date_pricing?.enabled && (
          <div className="col-span-full">
            <div className="grid sm:grid-cols-6 gap-4">
              <div className="sm:col-span-3">
                <Label id="pricing_by_date_type.holiday.original_price" name="Holiday Original Price" required />
                <div className="flex">
                  <Input
                    id="pricing_by_date_type.holiday.original_price"
                    type="number"
                    decimal={2}
                    className="rounded-r-none"
                    placeholder="Holiday original price"
                    disabled={watch("price_type") === PriceType.FREE}
                    error={get(errors, "pricing_by_date_type.holiday.original_price")}
                    {...register("pricing_by_date_type.holiday.original_price", {
                      valueAsNumber: true
                    })}
                  />
                  <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                    {eventDetailData.currency}
                  </span>
                </div>
                {errors["pricing_by_date_type"]?.holiday?.original_price ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["pricing_by_date_type"]?.holiday?.original_price?.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label id="pricing_by_date_type.holiday.price" name="Holiday Price" required />
                <div className="flex">
                  <Input
                    id="pricing_by_date_type.holiday.price"
                    type="number"
                    decimal={2}
                    className="rounded-r-none"
                    placeholder="Holiday price"
                    disabled={watch("price_type") === PriceType.FREE}
                    error={get(errors, "pricing_by_date_type.holiday.price")}
                    {...register("pricing_by_date_type.holiday.price", {
                      valueAsNumber: true
                    })}
                  />
                  <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                    {eventDetailData.currency}
                  </span>
                </div>
                {errors["pricing_by_date_type"]?.holiday?.price ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["pricing_by_date_type"]?.holiday?.price?.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label id="pricing_by_date_type.weekend.original_price" name="Weekend Original Price" required />
                <div className="flex">
                  <Input
                    id="pricing_by_date_type.weekend.original_price"
                    type="number"
                    decimal={2}
                    className="rounded-r-none"
                    placeholder="Weekend original price"
                    disabled={watch("price_type") === PriceType.FREE}
                    error={get(errors, "pricing_by_date_type.weekend.original_price")}
                    {...register("pricing_by_date_type.weekend.original_price", {
                      valueAsNumber: true
                    })}
                  />
                  <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                    {eventDetailData.currency}
                  </span>
                </div>
                {errors["pricing_by_date_type"]?.weekend?.original_price ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["pricing_by_date_type"]?.weekend?.original_price?.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-3">
                <Label id="pricing_by_date_type.weekend.price" name="Weekend Price" required />
                <div className="flex">
                  <Input
                    id="pricing_by_date_type.weekend.price"
                    type="number"
                    decimal={2}
                    className="rounded-r-none"
                    placeholder="Weekend price"
                    disabled={watch("price_type") === PriceType.FREE}
                    error={get(errors, "pricing_by_date_type.weekend.price")}
                    {...register("pricing_by_date_type.weekend.price", {
                      valueAsNumber: true
                    })}
                  />
                  <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                    {eventDetailData.currency}
                  </span>
                </div>
                {errors["pricing_by_date_type"]?.weekend?.price ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["pricing_by_date_type"]?.weekend?.price?.message}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        )}
        
        <div className="sm:col-span-3">
          <Label
            id="sell_from"
            name="Start date"
            required
          />
          <Controller
            control={control}
            name="sell_from"
            render={({ field: { onChange, value = "" } }) => {
              return (
                <DateTimePicker
                  id="sell_from"
                  placeholder="Start date"
                  error={get(
                    errors,
                    `sell_from.message`,
                    ""
                  )}
                  value={value}
                  onChange={(date: any) => date === null ? onChange("") : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))}
                />
              );
            }}
          />
          {get(
            errors,
            `sell_from`,
            ""
          ) ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `sell_from.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="sell_to"
            name="End date"
            required
          />
          <Controller
            control={control}
            name="sell_to"
            render={({ field: { onChange, value = "" } }) => {
              return (
                <DateTimePicker
                  id="sell_to"
                  placeholder="End date"
                  error={get(
                    errors,
                    `sell_to.message`,
                    ""
                  )}
                  value={value}
                  onChange={(date: any) => date === null ? onChange("") : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))}
                />
              );
            }}
          />
          {get(
            errors,
            `sell_to`,
            ""
          ) ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `sell_to.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>

        <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
          <p className="text-sm">Advance Settings</p>
        </div>
        <div className="col-span-full flex flex-col items-start gap-y-4">
          <Controller
            control={control}
            name="hide_in_ui"
            render={({ field: { onChange, value, ref } }) => (
              <SwitchButton label="Show in PROD" checked={!value} onChange={(isChedked) => onChange(!isChedked)} />
            )}
          />
          {get(errors, `hide_in_ui`, "") ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `hide_in_ui.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
          <Controller
            control={control}
            name="is_soldout"
            render={({ field: { onChange, value, ref } }) => (
              <SwitchButton label="Is it Sold Out ?" checked={value} onChange={onChange} />
            )}
          />
          {get(errors, `is_soldout`, "") ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `is_soldout.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>
        <div className="col-span-full">
          <Label
            id={`detailed_ticket_information_${eventDetailData.languages[0] ?? "en"}`}
            name="Detailed ticket information"
          />
          <div className="flex flex-col gap-y-4">
            {eventDetailData.languages.map(
              (lang: SupportedLanguage) => (
                <div key={lang}>
                  <div
                    className={`w-fit flex flex-row items-center gap-x-1 p-2 -mb-1 rounded-tl-md rounded-tr-md border-magnolia-600 border-solid border-[1px] bg-magnolia-400 font-medium text-sm`}
                  >
                    <img
                      src={`/images/flags/circle-${lang}.png`}
                      alt="Language Icon"
                      className="w-4 shrink-0"
                    />
                    <p>
                      {
                        LANGUAGES.find(
                          (language) =>
                            language.value === lang
                        )?.label
                      }
                    </p>
                  </div>
                  <Controller
                    control={control}
                    name={`detailed_ticket_information.${lang}`}
                    render={({
                      field: { onChange, value },
                    }) => (
                      <TextEditor
                        key={`detailed_ticket_information.${lang}`}
                        value={value ?? ""}
                        onChange={onChange}
                      />
                    )}
                  />
                  {get(
                    errors,
                    `detailed_ticket_information.${lang}`,
                    ""
                  ) ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(
                          errors,
                          `detailed_ticket_information.${lang}.message`,
                          ""
                        )}
                      </span>
                    </p>
                  ) : null}
                </div>
              )
            )}
          </div>
        </div>
      </div>

    </RightSideModal>
  )
}